import useApiPrivateClient from "../../hooks/useApiPrivateClient";
import {
  CreateAmbientSoundsData,
  CreatePOI,
  CreateRouteData,
  UpdateRouteAndTranslationsData,
  UpdateRouteData,
} from "./editRoute.requests";
import {
  AmbientSounds,
  EditOptions,
  POI,
  Point,
  PublishReadiness,
  Route,
  RouteState,
} from "./editRoute.responses";

export const useEditRouteService = () => {
  const apiPrivateClient = useApiPrivateClient();
  const editServiceBasePath: string = "/edits/routes";

  const getEditOptions = () => {
    return apiPrivateClient.get<EditOptions>(`${editServiceBasePath}/options`);
  };

  //does it matter what it returns?
  const createRoute = (createRouteData: CreateRouteData) => {
    return apiPrivateClient.post<RouteState>(
      `${editServiceBasePath}`,
      createRouteData
    );
  };

  const updateRoute = (routeId: string, updateRouteData: UpdateRouteData) => {
    return apiPrivateClient.patch<Route>(
      `${editServiceBasePath}/${routeId}`,
      updateRouteData
    );
  };

  const deleteRoute = (routeId: string) => {
    return apiPrivateClient.delete<Route>(`${editServiceBasePath}/${routeId}`);
  };

  const updateRouteAndTranslations = (
    routeId: string,
    updateRouteData: UpdateRouteAndTranslationsData
  ) => {
    return apiPrivateClient.put(
      `${editServiceBasePath}/${routeId}`,
      updateRouteData
    );
  };

  const getGeoLine = (routeId: string) => {
    return apiPrivateClient.get<Point[]>(
      `${editServiceBasePath}/${routeId}/geo-line`
    );
  };

  const updateGeoLine = (routeId: string, updateGeoLineData: Point[]) => {
    return apiPrivateClient.put<Point[]>(
      `${editServiceBasePath}/${routeId}/geo-line`,
      updateGeoLineData
    );
  };

  const getAmbientSounds = (routeId: string) => {
    return apiPrivateClient.get<AmbientSounds>(
      `${editServiceBasePath}/${routeId}/ambient-sounds`
    );
  };

  const streamSound = (
    pointId: number,
    routeEditId: string,
    pointType: "poi" | "ambient",
    languageCode?: string
  ) => {
    if (pointType === "ambient")
      return apiPrivateClient.get(
        `${editServiceBasePath}/stream/${pointId}?routeEditId=${routeEditId}&pointType=${pointType}`,
        { responseType: "blob" }
      );
    else {
      return apiPrivateClient.get(
        `${editServiceBasePath}/stream/${pointId}?routeEditId=${routeEditId}&pointType=${pointType}&languageCode=${languageCode}`,
        { responseType: "blob" }
      );
    }
  };

  const updateAmbientSounds = (
    routeId: string,
    updateAmbientSoundsData: CreateAmbientSoundsData
  ) => {
    return apiPrivateClient.post(
      `${editServiceBasePath}/${routeId}/ambient-sounds`,
      updateAmbientSoundsData
    );
  };

  const getPOIs = (routeId: string) => {
    return apiPrivateClient.get<POI[]>(
      `${editServiceBasePath}/${routeId}/pois`
    );
  };

  const updatePOIs = (routeId: string, updatePOIsData: CreatePOI[]) => {
    return apiPrivateClient.post(
      `${editServiceBasePath}/${routeId}/pois`,
      updatePOIsData
    );
  };

  const editRoute = (routeId: string) => {
    return apiPrivateClient.post(`${editServiceBasePath}/${routeId}/edit`);
  };

  const checkPublishReadiness = (routeId: string) => {
    return apiPrivateClient.get<PublishReadiness>(
      `${editServiceBasePath}/${routeId}/publish-readiness`
    );
  };

  const publishRoute = (routeId: string) => {
    return apiPrivateClient.post(`${editServiceBasePath}/${routeId}/publish`);
  };

  const getRouteState = (routeId: string) => {
    return apiPrivateClient.get<RouteState>(
      `${editServiceBasePath}/${routeId}`
    );
  };

  return {
    getEditOptions,
    createRoute,
    updateRoute,
    deleteRoute,
    updateRouteAndTranslations,
    getGeoLine,
    updateGeoLine,
    getAmbientSounds,
    updateAmbientSounds,
    getPOIs,
    updatePOIs,
    editRoute,
    checkPublishReadiness,
    publishRoute,
    getRouteState,
    streamSound,
  };
};

export default useEditRouteService;
